import React, { useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import styles from "./TrainingDetailsTable.module.css";

const GENDER_ROWS = [
  {
    label: "Male",
    totalKey: "totalMale",
    healthKey: "HealthMaleNumber",
    skillKey: "SkillMaleNumber",
    healthPctKey: "HealthMalePercentage",
    skillPctKey: "SkillMalePercentage",
    rowIndex: 0,
  },
  {
    label: "Female",
    totalKey: "totalFemale",
    healthKey: "HealthFemaleNumber",
    skillKey: "SkillFemaleNumber",
    healthPctKey: "HealthFemalePercentage",
    skillPctKey: "SkillFemalePercentage",
    rowIndex: 1,
  },
  {
    label: "Total",
    totalKey: "Total",
    healthKey: "HealthTotal",
    skillKey: "SkillTotal",
    healthPctKey: "HealthTotalPercentage",
    skillPctKey: "SkillTotalPercentage",
    rowIndex: 2,
    isTotal: true,
  },
];

const SECTIONS = [
  {
    label: "Employees",
    path: "employeeTrainingUpgradation",
    errorKey: "employees",
  },
  {
    label: "Workers",
    path: "workerTrainingUpgradation",
    errorKey: "workers",
  },
];

const calculatePercentage = (value, total) => {
  const hasValue = value !== "" && value !== null && value !== undefined;
  const hasTotal = total !== "" && total !== null && total !== undefined;
  if (!hasValue || !hasTotal) return " ";
  const numericValue = Number(value);
  const numericTotal = Number(total);
  if (numericTotal === 0) return numericValue === 0 ? 0 : " ";
  return Math.round((numericValue / numericTotal) * 100 * 100) / 100;
};

const sumValues = (...values) => {
  const hasAnyValue = values.some(
    (item) => item !== "" && item !== null && item !== undefined,
  );
  if (!hasAnyValue) return "";
  return values.reduce((sum, item) => sum + Number(item || 0), 0);
};

const getFieldPath = (financialYear, sectionPath, field) =>
  `trainingDetails.${financialYear}.${sectionPath}.${field}`;

const TrainingDetailsTable = ({
  financialYear = "currentFinancialYear",
  formValues,
  setFormValues,
  setRowIndex,
  errors,
  disabled,
}) => {
  useEffect(() => {
    const newErrors = {};
    SECTIONS.forEach((section) => {
      GENDER_ROWS.forEach((row) => {
        [row.totalKey, row.healthKey, row.skillKey].forEach((field) => {
          newErrors[getFieldPath(financialYear, section.path, field)] = {
            rowIndex: row.rowIndex,
            message: "",
          };
        });
      });
    });
    if (setRowIndex) {
      setRowIndex((prev) => ({ ...prev, ...newErrors }));
    }
  }, [financialYear, setRowIndex]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!/^\d*$/.test(value)) return;

    setFormValues((prevState) => {
      if (!prevState) return {};
      const keys = name.split(".");
      const updatedData = structuredClone(prevState);
      let currentLevel = updatedData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!currentLevel[keys[i]]) {
          currentLevel[keys[i]] = {};
        }
        currentLevel = currentLevel[keys[i]];
      }

      const lastKey = keys[keys.length - 1];
      currentLevel[lastKey] = value ? Number(value) : "";

      const parentObject = keys
        .slice(0, -1)
        .reduce((obj, key) => obj?.[key], updatedData);

      if (!parentObject || keys[0] !== "trainingDetails") {
        return updatedData;
      }

      const editingValue = parentObject?.[lastKey];
      const male =
        lastKey === "totalMale" ? editingValue : parentObject?.totalMale;
      const female =
        lastKey === "totalFemale" ? editingValue : parentObject?.totalFemale;
      const helthMale =
        lastKey === "HealthMaleNumber"
          ? editingValue
          : parentObject?.HealthMaleNumber;
      const helthFemale =
        lastKey === "HealthFemaleNumber"
          ? editingValue
          : parentObject?.HealthFemaleNumber;
      const skillMale =
        lastKey === "SkillMaleNumber"
          ? editingValue
          : parentObject?.SkillMaleNumber;
      const skillFemale =
        lastKey === "SkillFemaleNumber"
          ? editingValue
          : parentObject?.SkillFemaleNumber;

      const isEditingEmpty =
        editingValue === "" ||
        editingValue === null ||
        editingValue === undefined;

      if (lastKey === "totalMale" && isEditingEmpty) {
        parentObject.HealthMaleNumber = "";
        parentObject.SkillMaleNumber = "";
      }
      if (lastKey === "totalFemale" && isEditingEmpty) {
        parentObject.HealthFemaleNumber = "";
        parentObject.SkillFemaleNumber = "";
      }

      const isValidNumber = (v) =>
        v !== "" && v !== null && v !== undefined && !isNaN(Number(v));

      const exceedsRowTotal = (amount, rowTotal) => {
        if (!isValidNumber(amount) || !isValidNumber(rowTotal)) return false;
        return Number(amount) > Number(rowTotal);
      };

      if (!isEditingEmpty) {
        if (
          (lastKey === "HealthMaleNumber" &&
            exceedsRowTotal(editingValue, male)) ||
          (lastKey === "SkillMaleNumber" && exceedsRowTotal(editingValue, male)) ||
          (lastKey === "HealthFemaleNumber" &&
            exceedsRowTotal(editingValue, female)) ||
          (lastKey === "SkillFemaleNumber" &&
            exceedsRowTotal(editingValue, female))
        ) {
          return prevState;
        }

        if (lastKey === "totalMale" && isValidNumber(editingValue)) {
          if (
            exceedsRowTotal(helthMale, editingValue) ||
            exceedsRowTotal(skillMale, editingValue)
          ) {
            return prevState;
          }
        }

        if (lastKey === "totalFemale" && isValidNumber(editingValue)) {
          if (
            exceedsRowTotal(helthFemale, editingValue) ||
            exceedsRowTotal(skillFemale, editingValue)
          ) {
            return prevState;
          }
        }
      }

      parentObject.Total = sumValues(male, female);
      parentObject.HealthTotal = sumValues(helthMale, helthFemale);
      parentObject.HealthMalePercentage = calculatePercentage(helthMale, male);
      parentObject.HealthFemalePercentage = calculatePercentage(
        helthFemale,
        female,
      );
      parentObject.HealthTotalPercentage = calculatePercentage(
        parentObject.HealthTotal,
        parentObject.Total,
      );
      parentObject.SkillMalePercentage = calculatePercentage(skillMale, male);
      parentObject.SkillFemalePercentage = calculatePercentage(
        skillFemale,
        female,
      );
      parentObject.SkillTotal = sumValues(skillMale, skillFemale);
      parentObject.SkillTotalPercentage = calculatePercentage(
        parentObject.SkillTotal,
        parentObject.Total,
      );

      return updatedData;
    });
  };

  const getRowData = (sectionPath) =>
    formValues?.trainingDetails?.[financialYear]?.[sectionPath] ?? {};

  const renderNumberField = (sectionPath, field, isTotalRow) => {
    const name = getFieldPath(financialYear, sectionPath, field);
    const value = getRowData(sectionPath)[field] ?? "";

    return (
      <TextField
        variant="standard"
        disabled={isTotalRow || disabled}
        fullWidth
        size="small"
        name={name}
        value={value}
        placeholder="Enter Number"
        onChange={!isTotalRow && !disabled ? handleChange : undefined}
        InputProps={{ disableUnderline: true }}
        sx={{
          backgroundColor: isTotalRow ? "#EFEFEF" : "white",
          height: "100%",
          "& .MuiInputBase-root": { height: "100%", padding: 0 },
          "& .MuiInputBase-input": {
            padding: "0 8px",
            height: "100%",
            fontSize: "0.875rem",
            textAlign: "center",
          },
        }}
      />
    );
  };

  const renderPercentageField = (sectionPath, field) => {
    const value = getRowData(sectionPath)[field] ?? "";

    return (
      <TextField
        variant="standard"
        disabled
        fullWidth
        size="small"
        value={value}
        placeholder="%"
        InputProps={{ disableUnderline: true }}
        sx={{
          backgroundColor: "#EFEFEF",
          height: "100%",
          "& .MuiInputBase-root": { height: "100%", padding: 0 },
          "& .MuiInputBase-input": {
            padding: "0 8px",
            height: "100%",
            fontSize: "0.875rem",
            textAlign: "center",
          },
        }}
      />
    );
  };

  return (
    <Box>
      <TableContainer className={styles.tableContainer}>
        <Table className={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} className={styles.headCell} />
              <TableCell rowSpan={2} className={styles.headCell}>
                Category
              </TableCell>
              <TableCell rowSpan={2} className={styles.headCell}>
                Total
              </TableCell>
              <TableCell colSpan={2} className={styles.headCell}>
                On Health and safety measures
              </TableCell>
              <TableCell colSpan={2} className={styles.headCell}>
                On skill upgradation
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={styles.subHeadCell}>Number</TableCell>
              <TableCell className={styles.subHeadCell}>Percentage</TableCell>
              <TableCell className={styles.subHeadCell}>Number</TableCell>
              <TableCell className={styles.subHeadCell}>Percentage</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {SECTIONS.map((section) =>
              GENDER_ROWS.map((row, rowIdx) => (
                <TableRow
                  key={`${section.path}-${row.label}`}
                  className={row.isTotal ? styles.totalRow : undefined}
                >
                  {rowIdx === 0 && (
                    <TableCell
                      rowSpan={GENDER_ROWS.length}
                      className={styles.groupCell}
                    >
                      {section.label}
                    </TableCell>
                  )}
                  <TableCell className={styles.categoryCell}>
                    {row.label}
                  </TableCell>
                  <TableCell className={styles.inputCell}>
                    {renderNumberField(section.path, row.totalKey, row.isTotal)}
                  </TableCell>
                  <TableCell className={styles.inputCell}>
                    {renderNumberField(section.path, row.healthKey, row.isTotal)}
                  </TableCell>
                  <TableCell className={styles.inputCell}>
                    {renderPercentageField(section.path, row.healthPctKey)}
                  </TableCell>
                  <TableCell className={styles.inputCell}>
                    {renderNumberField(section.path, row.skillKey, row.isTotal)}
                  </TableCell>
                  <TableCell className={styles.inputCell}>
                    {renderPercentageField(section.path, row.skillPctKey)}
                  </TableCell>
                </TableRow>
              )),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {Object.entries(errors?.employees ?? {}).map(([rowIndex, items]) => (
        <Typography
          variant="body2"
          key={`error-employees-${rowIndex}`}
          className={styles.errorText}
        >
          Row employees{rowIndex}: Missing fields -{" "}
          {items.map((item) => item.fieldName).join(", ")}
        </Typography>
      ))}

      {Object.entries(errors?.workers ?? {}).map(([rowIndex, items]) => (
        <Typography
          variant="body2"
          key={`error-workers-${rowIndex}`}
          className={styles.errorText}
        >
          Row workers{rowIndex}: Missing fields -{" "}
          {items.map((item) => item.fieldName).join(", ")}
        </Typography>
      ))}
    </Box>
  );
};

export default TrainingDetailsTable;
